import { useTheme } from '@emotion/react'
import { Add, DeleteOutlineRounded, Description, Edit, EditOutlined, Search } from '@mui/icons-material'
import { Box, Button, capitalize, Chip, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material'
// import { FetchAllStartupFeedbacks } from 'api/profile'
import { FetchAllRounds } from 'api/round'
import Cookies from 'js-cookie'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AsyncTableListing from 'ui-components/AsyncTableListing'
import { formatAllowsNegativeCurrency, handleDateValues, hasPermission, plans, transformPlans } from 'utils/index'
import DeleteConfirmationDialog from 'ui-components/popups/DeleteConfirmationPopup'
// import placeHolderImage from "assets/no_data_images/no_data_rounds.svg"
import { Primary } from 'ui-components/Buttons';import CustomInputField from 'ui-components/CustomInputField'
import { DeleteUserDataByID, FetchAllUsersData } from 'api/settings'
import toast from 'react-hot-toast'
import toast_message from "utils/toast_message.json"
import { FetchPermissionsTemplate } from 'api/permission'
import { useToast } from 'context/ToastContext'
import { DeleteOfferCode, FetchAllOfferCodesData } from 'api/admin_settings'
import UpdatePromoCode from './popups/UpdatePromoCode'
import moment from 'moment'
import { FetchAllStartupPlans } from 'api/utils'


/**
 * The plans an offer code applies to, one chip each. A code can cover several
 * plans across two versions, and as a comma-joined sentence that is unreadable
 * at a glance — the version in particular gets lost mid-string.
 *
 * Current plans are tinted and listed first; superseded ones stay muted, so the
 * common question ("does this code still apply to anything we sell?") is
 * answerable without reading the text.
 */
const ApplicablePlans = ({ plans = [] }) => {
  if (!plans?.length) return <span>-</span>;

  const ordered = [...plans].sort((a, b) => (b.version || 0) - (a.version || 0));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', paddingBlock: '4px' }}>
      {ordered.map((plan, index) => {
        const isCurrent = !plan.version || plan.version > 1;

        return (
          <Chip
            key={`${plan.label}-${index}`}
            size="small"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                {plan.label}
                {plan.version ? (
                  <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, opacity: 0.75 }}>
                    {`V${plan.version}`}
                  </Box>
                ) : null}
              </Box>
            }
            sx={{
              height: '24px',
              fontSize: '12px',
              fontWeight: 500,
              borderRadius: '6px',
              backgroundColor: isCurrent ? '#EEF2FF' : '#F3F4F6',
              color: isCurrent ? '#4338CA' : '#6B7280',
              border: `1px solid ${isCurrent ? '#C7D2FE' : '#E5E7EB'}`,
            }}
          />
        );
      })}
    </Box>
  );
};

const PromoCodes = ({ refetchListing, searchStatus, setSearchStatus, reset, search, setSearch, setReset, queryParams, setQueryParams, }) => {
  const theme = useTheme();
  const { addToast } = useToast();
  let [investor_id, setStartupID] = React.useState(Cookies.get("X-ADMIN-ID") || null);
  const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate()
  const columns = [
    { Header: 'Offer Code', accessor: 'offer_code', minWidth: 80, align: 'start', fixed: true },
    { Header: 'Discount Type', accessor: 'formatted_discount_type', minWidth: 80, align: 'center' },
    {
      Header: 'Applicable On', accessor: 'formatted_plan', minWidth: 260, align: 'center',
      Cell: ({ value }) => <ApplicablePlans plans={value} />
    },
    // { Header: 'Discount Value', accessor: 'formatted_discount_value', minWidth: 80, align: 'center' },
    // { Header: 'Offer Type', accessor: 'offer_type', minWidth: 80, align: 'center' },
    { Header: 'Valid From', accessor: 'formatted_valid_from', minWidth: 120, align: 'center' },
    { Header: 'Valid To', accessor: 'formatted_valid_to', minWidth: 120, align: 'center' },
    { Header: 'Status', accessor: 'formatted_status', minWidth: 120, align: 'center' },
    { Header: 'Actions', accessor: 'actions', minWidth: 100, align: 'end' },
  ];


  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [sort, setSort] = React.useState(-1);
  // const [search, setSearch] = React.useState(-1);
  const { data: userData, isLoading: isLoadingUserData, refetch: refetchPromoCodes } = FetchAllOfferCodesData({ investor_id, limit, page, searchText: search, enabled: true });
  const { data: startupPlans, isLoading: isStartupPlansLoading } = FetchAllStartupPlans();

  const {
    mutateAsync: deleteUserDataAsync,
    isLoading: isDeletingUserData,
    status: deleteUserDataStatus,
    error: deleteUserDataError,
    data: deleteUserDataResponse
  } = DeleteOfferCode();

  const planOptions = React.useMemo(() => transformPlans(startupPlans), [startupPlans]);

  const formattedUsersData = React.useMemo(() => {
    const dataArray = userData?.data || [];
    // console.log(dataArray, "dataArray")
    return !isLoadingUserData ? dataArray?.map(promo => {
      return ({
        ...promo,
        formatted_discount_type: capitalize(promo?.discount_type),
        // Kept as structured values rather than a joined string so the cell can
        // render one chip per plan; a code applying to five plans is unreadable
        // as prose.
        formatted_plan: promo.plan_id?.map(item => {
          const plan = planOptions.find(p => p.value === item);
          if (!plan) return { label: item, version: null };
          return {
            label: `${plan.plan_title || ''} ${capitalize(plan.frequency || '')}`.trim(),
            version: plan.version,
          };
        }) || [],
        formatted_status: capitalize(promo?.status),
        // formatted_discount_value: promo.discount_type === "percentage" ? promo?.discount_value + "%" : "₹" + formatAllowsNegativeCurrency(promo?.discount_value, "en-IN")?.replaceAll("-", ""),
        formatted_valid_from: promo?.valid_from ? moment(promo?.valid_from).format("DD-MMM-YYYY") : "-",
        formatted_valid_to: promo?.valid_to ? moment(promo?.valid_to).format("DD-MMM-YYYY") : "-"
      })
    }) : []
  }, [userData, isLoadingUserData, planOptions])

  const [currentRow, setCurrentRow] = React.useState(null);
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);

  const handleOpenDeletePopup = () => {
    setShowDeletePopup(true);
  }

  const handleCloseDeletePopup = () => {
    setShowDeletePopup(false);
  }

  const handleDeleteOfferCode = () => {
    // console.log(currentRow?._id,"ddd")
    deleteUserDataAsync({ _id: currentRow?._id }, {
      onSuccess: () => {
        refetchPromoCodes();
        handleCloseDeletePopup();
        handleClosePromoCodeCreatePopup()
        // toast.success(toast_message?.users?.delete)
        addToast("Offer Code Deleted!", "success")
      },
      onError: () => {
      }
    })
  }
  React.useEffect(() => {
    refetchPromoCodes();
  }, [reset]);

  // Cross inside the search field clears the search term.
  const handleSearchReset = () => {
    setSearch('');
    Cookies.set("search_startup_settings", "");
    setSearchStatus(false);
    setPage(1);
    setReset(!reset);
  }

  const [openPromoCodeCreate, setOpenPromoCodeCreate] = React.useState(false);

  const handleOpenPromoCodeCreatePopup = () => {
    setOpenPromoCodeCreate(true)
  }

  const handleClosePromoCodeCreatePopup = () => {
    setOpenPromoCodeCreate(false);
    setCurrentRow(null)
  }

  const handleUpdatePromoCodePopup = ({ row }) => {
    // console.log(row, "row")
    setCurrentRow(row?.original)
    setOpenPromoCodeCreate(true)
  }


  const RowActions = ({ row }) => {
    const currentLocation = window.location;


    // console.log(row?.original,"row")
    return (!row?.original?.is_owner ? <div style={{ display: "flex", marginLeft: 'auto' }}>
      <Tooltip title="Edit">
        <Button sx={{ minWidth: "1rem" }} onClick={() => {
          handleUpdatePromoCodePopup({ row: row })
          // Cookies.set("settings_user_id", row?.original?._id)
          // refetchPermissionsTemplate();
          // if (currentLocation.pathname?.startsWith("/admin_settings")) {
          //   navigate("/admin_settings/admin_users/add_user")
          // }
          // else {
          // navigate("/admin_settings/admin_users/edit_user")
          // }
        }}>
          <EditOutlined
            sx={{ color: theme.palette.customColors.indigo700 }}

          />
        </Button>
      </Tooltip>
      <Tooltip title="Delete">
        <Button
          sx={{ minWidth: "1rem" }}
          onClick={() => {
            setCurrentRow(row?.original);
            handleOpenDeletePopup()
          }}
        >
          <DeleteOutlineRounded
            sx={{ color: theme.palette.customColors.indigo700 }} />
        </Button>
      </Tooltip>
    </div> : "")
  }

  return (
    <>
      <UpdatePromoCode
        open={openPromoCodeCreate}
        handleClose={handleClosePromoCodeCreatePopup}
        selectedItem={currentRow}
        onSuccess={() => refetchPromoCodes()}
      />
      <DeleteConfirmationDialog open={showDeletePopup} handleClose={handleCloseDeletePopup} handleSubmission={handleDeleteOfferCode} heading={"Delete Offer Code"} ActionText={"Confirm"} subheading={"Are you sure you want to delete this offer code?"} />
      <Grid container xs={12} pt={1.1} pb={0} spacing={2} display={"flex"} justifyContent={"space-between"} marginLeft={0} alignContent={"flex-start"} >
        {isLoadingUserData ?

          <Grid item xs={12} height={"100dvh"} sx={{ width: '100%', padding: "2rem", paddingLeft: "2rem !important", paddingTop: "0rem !important", bgColor: theme.palette.customColors.yellow_bg }} paddingInline={{ xs: "0.5rem !important", sm: '1rem !important', md: '1rem !important', lg: '2rem !important' }}>
            <Box className='skeleton' minHeight={"70dvh"} sx={{ borderRadius: "8px", border: `1px solid ${theme.palette.customColors.b50}` }} marginTop={0}>

            </Box>
          </Grid>
          :
          <Box width={"100%"} paddingLeft={"2rem"} maxWidth={"100%"} overflow={'hidden'} paddingInline={{ xs: "0.5rem !important", sm: '1rem !important', md: '1rem !important', lg: '2rem !important' }}>
            <Grid container padding={"0.5rem"} sx={{
              border: `1px solid ${theme.palette.customColors.b50} !important`,
              borderRadius: '8px 8px 0px 0px',
              background: theme?.palette.customColors.whiteBG,
            }}>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={3} style={{ display: 'flex', height: 'min-content', alignItems: 'center', gap: "0.5rem" }}>
                <CustomInputField
                  autoComplete="off"
                  sx={{
                    marginBottom: 0,
                    "& > .MuiFilledInput-root": {
                      borderColor: `${theme.palette.customColors.b50} !important`,
                      borderRadius: '8px'
                    }
                  }}
                  // disabled={true}
                  fullWidth
                  value={search}
                  name={`search_${Math.random().toString(36).substring(7)}`}  // Set a random name attribute
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      // alert(e.target.value);
                      refetchPromoCodes()
                      if (search) {
                        setSearchStatus(true)
                      }
                      setPage(1);
                    }
                  }}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    Cookies.set("search_startup_settings", event.target.value);
                    setSearchStatus(false)
                  }}
                  size="small"
                  startAdornment={<>
                    <Search sx={{ padding: "0rem 0rem 0rem 0.5rem" }} />
                  </>}
                  onClear={handleSearchReset}
                  placeholder={'Search by offer code'}
                />

                {/* <Typography sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  lineHeight: '24px',
                  textAlign: "center",
                  textTransform: 'capitalize',
                  color: theme.palette.customColors.g300,
                  // padding: "1rem 1rem 1rem 1rem",
                  marginLeft: "1rem",
                  // marginRight: 'auto',
                  width: "max-content",
                  textDecoration: "underline",
                  textUnderlineOffset: "1rem",
                  paddingTop: '0.5rem',
                  textDecorationThickness: "1.5px",
                  textDecorationColor: theme => theme.palette.customColors.indigo700
                }}>
                  Offer Codes
                </Typography> */}


              </Grid>
              <Grid item width="25%" style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', marginLeft: "auto" }}>
                <Primary
                  onClick={() => {
                    handleOpenPromoCodeCreatePopup()
                    // Cookies.remove("settings_user_id");
                    // navigate("/admin_settings/admin_users/add_user");
                  }}
                  sx={{
                    // bgcolor: theme.palette.customColors.indigo600,
                    // border: `1px solid ${theme.palette.customColors.indigo600} `,
                    padding: '10px 16px',
                    fontWeight: 600,
                    minWidth: isSmallScreen ? "0" : '64px',
                    '& > .MuiButton-icon.MuiButton-startIcon': {
                      marginRight: isSmallScreen ? 0 : '8px',
                      marginLeft: isSmallScreen ? 0 : '-4px'
                    }
                  }}
                  startIcon={<Add fontSize='1.25rem' />}
                >
                  {!isSmallScreen ? "Create Offer Code" : "Create"}
                </Primary>
              </Grid>
            </Grid>

            <Grid id="users-settings-table">
              <AsyncTableListing
                actions={() => <Primary
                  onClick={() => {
                    handleOpenPromoCodeCreatePopup()
                    // Cookies.remove("settings_user_id");
                    // navigate("/admin_settings/admin_users/add_user");
                  }}
                  sx={{
                    // bgcolor: theme.palette.customColors.indigo600,
                    // border: `1px solid ${theme.palette.customColors.indigo600} `,
                    padding: '10px 16px',
                    fontWeight: 600,
                    minWidth: isSmallScreen ? "0" : '64px',
                    '& > .MuiButton-icon.MuiButton-startIcon': {
                      marginRight: isSmallScreen ? 0 : '8px',
                      marginLeft: isSmallScreen ? 0 : '-4px'
                    }
                  }}
                  startIcon={<Add fontSize='1.25rem' />}
                >
                  {!isSmallScreen ? "Create Offer Code" : "Create"}
                </Primary>}
                // isLoading={isLoadingFeedbacks}
                columns={columns}
                data={formattedUsersData}
                page={page}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                count={10}
                RowActions={RowActions}
                // tabValue={value}
                pagination={false}
                noDataTitle={search?.length > 0 ? 'No Search Results for "' + search + '"' : "No Offer Codes"}
                sx={{
                  borderRadius: '0px 0px 8px 8px',
                  border: `1px solid ${theme.palette.customColors.b50}`,
                  bgcolor: "white"
                }}
              />
            </Grid>
          </Box>
        }

      </Grid>
    </>
  )
}

export default PromoCodes
